import type { SubscriptionStatus } from "../../types";
import type {
  CustomerData,
  InvoiceData,
  PaymentMethodData,
  PaystackAuthorization,
  PaystackCustomer,
  PaystackSubscription,
  PaystackTransaction,
  SubscriptionData,
} from "./types";
import type {
  AnyPaystackEvent,
  ChargeSuccessEvent,
  InvoiceCreateEvent,
  InvoicePaymentFailedEvent,
  InvoiceUpdateEvent,
  SubscriptionCreateEvent,
  SubscriptionDisableEvent,
  SubscriptionExpiringCardsEvent,
  SubscriptionNotRenewEvent,
} from "./webhook-types";

export function normalizePaystackStatus(raw: string): SubscriptionStatus {
  switch (raw) {
    case "active":
      return "active";
    case "attention":
      return "attention";
    case "non-renewing":
      return "non_renewing";
    case "cancelled":
      return "cancelled";
    case "complete":
    case "completed":
      return "completed";
    default:
      return "cancelled";
  }
}

export function derivePaystackEventId(event: AnyPaystackEvent): string {
  switch (event.event) {
    case "charge.success":
      return (event as ChargeSuccessEvent).data.reference;

    // All three share one id, so a "duplicate" invoice event is often a
    // different event about the same invoice, not a redelivery. Failure
    // handling therefore dedupes on the retry row, not on this id — see
    // handleFailedRenewal in apps/billing/lib/webhooks/paystack-dispatcher.ts.
    case "invoice.create":
    case "invoice.update":
    case "invoice.payment_failed":
      return (
        event as
          | InvoiceCreateEvent
          | InvoiceUpdateEvent
          | InvoicePaymentFailedEvent
      ).data.invoice_code;

    case "subscription.create":
      return `${event.event}:${(event as SubscriptionCreateEvent).data.subscription_code}`;

    case "subscription.not_renew":
      return `${event.event}:${(event as SubscriptionNotRenewEvent).data.subscription_code}`;

    case "subscription.disable":
      return `${event.event}:${(event as SubscriptionDisableEvent).data.subscription_code}`;

    case "subscription.expiring_cards": {
      // Batch event — derive a composite key from all subscription codes
      const codes = (event as SubscriptionExpiringCardsEvent).data
        .map((item) => item.subscription.subscription_code)
        .join(",");
      return `${event.event}:${codes}`;
    }

    case "refund.pending":
    case "refund.processing":
    case "refund.processed":
    case "refund.failed":
      return `${event.event}:${event.data.transaction_reference}`;

    default:
      // Exhaustiveness fallback
      return `unknown:${JSON.stringify(event)}`;
  }
}

export function convertSubunitsToMajor(amount: number): number {
  return amount / 100;
}

export function getCustomerDisplayName(
  customer: PaystackCustomer | CustomerData
): string {
  if (customer.first_name || customer.last_name) {
    return `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim();
  }
  return customer.email.split("@")[0] ?? customer.email;
}

export function transformPaystackCustomer(
  customer: PaystackCustomer
): CustomerData {
  return {
    ...customer,
    name: getCustomerDisplayName(customer),
    display_name: getCustomerDisplayName(customer),
  };
}

export function transformPaystackSubscription(
  subscription: PaystackSubscription
): SubscriptionData {
  return {
    id: subscription.id.toString(),
    subscription_code: subscription.subscription_code,
    status:
      subscription.status === "complete" ? "cancelled" : subscription.status,
    plan: {
      id: subscription.plan.id.toString(),
      plan_code: subscription.plan.plan_code,
      name: subscription.plan.name,
      amount: convertSubunitsToMajor(subscription.plan.amount),
      currency: subscription.plan.currency,
      interval:
        subscription.plan.interval === "annually"
          ? "yearly"
          : subscription.plan.interval,
      description: subscription.plan.description,
    },
    current_period_start: subscription.createdAt,
    current_period_end: subscription.next_payment_date,
    next_payment_date: subscription.next_payment_date,
    cancel_at_period_end: subscription.status === "non-renewing",
    email_token: subscription.email_token,
    quantity: subscription.quantity,
  };
}

export function transformPaystackAuthorization(
  auth: PaystackAuthorization
): PaymentMethodData {
  return {
    id: auth.authorization_code,
    type: "card",
    card: {
      brand: auth.brand,
      last4: auth.last4,
      exp_month: Number.parseInt(auth.exp_month, 10),
      exp_year: Number.parseInt(auth.exp_year, 10),
      bank: auth.bank,
      card_type: auth.card_type,
      bin: auth.bin,
    },
    is_default: true,
    reusable: auth.reusable,
    account_name: auth.account_name,
  };
}

export function transformPaystackTransaction(
  transaction: PaystackTransaction
): InvoiceData {
  return {
    id: transaction.id.toString(),
    transaction_id: transaction.id,
    reference: transaction.reference,
    status: transaction.status === "success" ? "paid" : transaction.status,
    amount: convertSubunitsToMajor(transaction.amount),
    currency: transaction.currency,
    created: transaction.created_at,
    paid_at: transaction.paid_at,
    description: transaction.plan?.name ?? `Payment via ${transaction.channel}`,
    gateway_response: transaction.gateway_response,
    channel: transaction.channel,
    fees: convertSubunitsToMajor(transaction.fees),
    plan_name: transaction.plan?.name ?? null,
    authorization: transaction.authorization
      ? {
          brand: transaction.authorization.brand,
          last4: transaction.authorization.last4,
        }
      : null,
  };
}
